import { StandardEvents } from '@shopify/events';

/**
 * Keeps a small piece of server-rendered product markup in step with the selected variant.
 *
 * Horizon's variant picker dispatches `shopify:product:select` and resolves `event.promise` with the re-rendered
 * page as `detail.html`. This element finds its twin in that document by `data-sync-id` and copies the children
 * across, so SKUs and prices come from Liquid, never from client-side formatting.
 */
class LscProductSync extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#scope = null;
  }

  /**
   * @param {Event & { promise?: Promise<{ detail?: any }> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct, productId } = detail;
        if (!newProduct && productId && this.dataset.productId && productId !== this.dataset.productId) return;

        const syncId = this.dataset.syncId ?? '';
        const next = html.querySelector(`lsc-product-sync[data-sync-id="${CSS.escape(syncId)}"]`);
        if (!next) return;

        if (newProduct) this.dataset.productId = newProduct.id;
        this.replaceChildren(...Array.from(next.childNodes).map((node) => document.importNode(node, true)));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[lsc-product-sync] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('lsc-product-sync')) {
  customElements.define('lsc-product-sync', LscProductSync);
}
